import type {
  AttendanceCustomPeriodEmployeeItemFragment,
  AttendanceEmployeesSettingComplementaryHoursBaselineEnum,
  AttendanceEmployeesSettingExtraHoursBaselineEnum,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'

import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'

import AttendanceMinutesCalculationUtils from 'modules/attendance/lib/utils/AttendanceMinutesCalculationUtils'
import ExtraHoursUtils from 'modules/attendance/lib/utils/ExtraHoursUtils'

/**
 * Union type for both extra hours and complementary hours baselines
 */
type BaselineEnum =
  | AttendanceEmployeesSettingExtraHoursBaselineEnum
  | AttendanceEmployeesSettingComplementaryHoursBaselineEnum

/**
 * Captures the operands used to derive extra time so the UI can explain the breakdown.
 */
type ExtraTimeInfo = {
  minutes: number | undefined
  baseline: BaselineEnum | undefined
  baselineMinutes: number
  comparedMinutes: number
}

type BaselineInfo = {
  type: BaselineEnum | undefined
  baselineMinutes: number
  comparedMinutes: number
}

type AggregateMinutesResult = {
  balanceMinutes: number
  plannedMinutes: number
  contractMinutes: number
  workedMinutes: number
  regularMinutes: number
  workedRegularMinutes: number
  paidBreaksMinutes: number
  paidAbsencesMinutes: number
  dailyDays: number
  effectiveWorkedMinutes: number
  workedExtraMinutes: number
  baseline: BaselineInfo
  extraTime: ExtraTimeInfo
  complementaryTime: ExtraTimeInfo
  extraHoursCalculationOn: string | undefined
  balancedRegularMinutes: number
}

const getRelevantBalance = (employee: AttendanceCustomPeriodEmployeeItemFragment, today: Date) => {
  const todayString = i18nDateToString(today, 'backend')
  const groupedBalances = groupBy(employee.attendanceBalances, 'date')
  const todayBalance = groupedBalances[todayString]?.[0]
  const lastBalance = employee.attendanceBalances[employee.attendanceBalances.length - 1]

  const isLastBalanceFromTodayOrBefore = stringToDate(
    lastBalance?.date ?? '',
    'backend'
  )?.isSameOrBefore(today)

  return isLastBalanceFromTodayOrBefore ? lastBalance : todayBalance
}

const getExtraHoursCalculationOn = (
  relevantBalance: { extraTimeCalculationOn?: string } | undefined
) => {
  const extraHoursCalculationOn = stringToDate(
    relevantBalance?.extraTimeCalculationOn ?? '',
    'backend'
  )

  return extraHoursCalculationOn
    ? i18nDateToString(extraHoursCalculationOn, 'ui_dmy_long')
    : undefined
}

const sumDailyBalanceUntil = (employee: AttendanceCustomPeriodEmployeeItemFragment) =>
  // Backend already trims balances to the requested period, so we simply add everything the
  // payload contains instead of enforcing an extra frontend cutoff.
  employee.attendanceBalances?.reduce(
    (acc, node) => acc + Number(node.dailyBalanceFromPlanning),
    0
  ) ?? 0

const sumPlannedMinutesUntil = (employee: AttendanceCustomPeriodEmployeeItemFragment) =>
  // Planned minutes arrive constrained by the server-selected window (start/endOn), which avoids
  // double counting future estimates on the client.
  employee.attendanceEstimatedTimes?.reduce((acc, node) => acc + Number(node.minutes ?? 0), 0) ?? 0

const sumContractMinutes = (employee: AttendanceCustomPeriodEmployeeItemFragment) =>
  employee.attendanceEstimatedTimes?.reduce(
    (acc, node) => acc + Number(node.contractMinutes ?? 0),
    0
  ) ?? 0

const calculateBaselineInfo = ({
  baseline,
  plannedMinutes,
  contractMinutes,
  effectiveWorkedMinutes,
  workedMinutes,
}: {
  baseline: BaselineEnum | undefined
  plannedMinutes: number
  contractMinutes: number
  effectiveWorkedMinutes: number
  workedMinutes: number
}): BaselineInfo => {
  // Without an explicit policy baseline we fall back to the backend balance pairing – planned
  // vs effective worked time – so the UI continues to present a meaningful delta.
  if (!baseline) {
    return {
      type: undefined,
      baselineMinutes: plannedMinutes,
      comparedMinutes: effectiveWorkedMinutes,
    }
  }

  const isContractBaseline = baseline === 'contract_hours'

  return {
    type: baseline,
    baselineMinutes: isContractBaseline ? contractMinutes : plannedMinutes,
    comparedMinutes: isContractBaseline ? effectiveWorkedMinutes : workedMinutes,
  }
}

/**
 * Normalises the backend-provided attendance totals into the structure used by the
 * time-tracking UI, preserving backend calculations (regular/effective minutes, balance,
 * extra time) so the breakdown always matches server figures.
 */
export const aggregateMinutes = ({
  employee,
  extraHoursBaselineOverride,
  complementaryHoursBaselineOverride,
  complementaryHoursFeatureEnabled = false,
}: {
  employee: AttendanceCustomPeriodEmployeeItemFragment
  extraHoursBaselineOverride?: AttendanceEmployeesSettingExtraHoursBaselineEnum
  complementaryHoursBaselineOverride?: AttendanceEmployeesSettingComplementaryHoursBaselineEnum
  /** Whether the complementary hours feature flag is enabled. Used to determine if part-time employees should show extra hours as fallback. */
  complementaryHoursFeatureEnabled?: boolean
}): AggregateMinutesResult => {
  const now = new Date()
  const relevantBalance = getRelevantBalance(employee, now)

  const workedMinutes = employee.attendanceAggregatedWorkedTime?.minutes ?? 0
  const paidAbsencesMinutes = employee.attendanceAggregatedWorkedTime?.paidAbsencesMinutes ?? 0
  const paidBreaksMinutes = employee.attendanceAggregatedWorkedTime?.paidBreaksMinutes ?? 0
  const effectiveWorkedMinutes =
    employee.attendanceAggregatedWorkedTime?.effectiveWorkedMinutes ??
    workedMinutes + paidAbsencesMinutes
  const dailyDays = employee.attendanceAggregatedWorkedTime?.dailyDays ?? 0

  const regularMinutes = employee.attendanceAggregatedWorkedTime?.regularMinutes ?? 0
  const workedRegularMinutes = employee.attendanceAggregatedWorkedTime?.workedRegularMinutes ?? 0
  const plannedMinutes = sumPlannedMinutesUntil(employee)
  const contractMinutes = sumContractMinutes(employee)
  const balanceMinutes = sumDailyBalanceUntil(employee)

  const extraHoursBaseline =
    extraHoursBaselineOverride ??
    employee.attendanceEmployeesPolicy?.attendanceEmployeesSettingsConnection?.edges?.[0]?.node
      ?.extraHoursBaseline ??
    undefined

  const complementaryHoursBaseline =
    complementaryHoursBaselineOverride ??
    employee.attendanceEmployeesPolicy?.attendanceEmployeesSettingsConnection?.edges?.[0]?.node
      ?.complementaryHoursBaseline ??
    undefined

  // Calculate baseline info for extra hours
  const extraHoursBaselineInfo = calculateBaselineInfo({
    baseline: extraHoursBaseline,
    plannedMinutes,
    contractMinutes,
    effectiveWorkedMinutes,
    workedMinutes,
  })

  // Calculate baseline info for complementary hours
  const complementaryHoursBaselineInfo = calculateBaselineInfo({
    baseline: complementaryHoursBaseline,
    plannedMinutes,
    contractMinutes,
    effectiveWorkedMinutes,
    workedMinutes,
  })

  // Worked extra minutes come directly from the backend aggregation (extra time blocks).
  const workedExtraMinutes = employee.attendanceAggregatedWorkedTime?.extraTimeMinutes ?? 0
  const hasExtraTimeCalculation = relevantBalance?.extraTimeCalculated || false

  const approvedExtraTimeMinutes =
    employee.attendanceAggregatedWorkedTime?.approvedExtraTimeMinutes ?? 0

  const balances = employee.attendanceBalances ?? []

  const aggregatedExtraHoursMinutes = ExtraHoursUtils.sumExtraHoursWithinRange({
    balances,
  })

  const balancedRegularMinutes = AttendanceMinutesCalculationUtils.calculateBalancedRegularMinutes({
    regularMinutes,
    approvedWorkedExtraMinutes: approvedExtraTimeMinutes,
    extraHoursMinutesFromBalance: aggregatedExtraHoursMinutes,
  })

  const aggregatedComplementaryTimeMinutes = ExtraHoursUtils.sumComplementaryTimeWithinRange({
    balances,
  })

  // Only surface extra time when the backend flags that the calculation is ready; otherwise
  // we keep it undefined to avoid drifting from backend totals.

  // For part-time employees:
  // - If complementary hours feature is OFF: show extra hours (fallback behavior)
  // - If complementary hours feature is ON and policy setting is ON: show complementary hours instead
  // - If complementary hours feature is ON but policy setting is OFF: show extra hours
  const isPartTime = employee.referenceContract?.partTime ?? false
  const isComplementaryHoursEnabledInPolicy =
    employee.attendanceEmployeesPolicy?.attendanceEmployeesSettingsConnection?.edges?.[0]?.node
      ?.enableComplementaryHours ?? false

  // Only suppress extra hours for part-time if they will see complementary hours instead
  // (requires both feature flag AND policy setting to be enabled)
  const shouldUseComplementaryHours =
    complementaryHoursFeatureEnabled && isPartTime && isComplementaryHoursEnabledInPolicy

  const extraTimeMinutes =
    extraHoursBaselineInfo.type && hasExtraTimeCalculation && !shouldUseComplementaryHours
      ? aggregatedExtraHoursMinutes
      : undefined

  // Only show complementary hours if the feature is enabled AND policy allows it AND employee is part-time
  const complementaryTimeMinutes =
    complementaryHoursBaselineInfo.type && hasExtraTimeCalculation && shouldUseComplementaryHours
      ? aggregatedComplementaryTimeMinutes
      : undefined

  const extraHoursCalculationOn = getExtraHoursCalculationOn(relevantBalance)

  return {
    balanceMinutes,
    plannedMinutes,
    contractMinutes,
    workedMinutes,
    regularMinutes,
    workedRegularMinutes,
    paidBreaksMinutes,
    paidAbsencesMinutes,
    dailyDays,
    effectiveWorkedMinutes,
    workedExtraMinutes,
    baseline: extraHoursBaselineInfo,
    extraTime: {
      minutes: extraTimeMinutes,
      baseline: extraHoursBaselineInfo.type,
      baselineMinutes: extraHoursBaselineInfo.baselineMinutes,
      comparedMinutes: extraHoursBaselineInfo.comparedMinutes,
    },
    complementaryTime: {
      minutes: complementaryTimeMinutes,
      baseline: complementaryHoursBaselineInfo.type,
      baselineMinutes: complementaryHoursBaselineInfo.baselineMinutes,
      comparedMinutes: complementaryHoursBaselineInfo.comparedMinutes,
    },
    extraHoursCalculationOn,
    balancedRegularMinutes,
  }
}

export type { AggregateMinutesResult }
